
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdArrowBack, MdSave, MdAdd } from 'react-icons/md';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import './AddProject.css';
import Swal from 'sweetalert2';
import Select from 'react-select';
import axios from 'axios';
import {
    CREATE_PROJECT_API,
    GET_ALL_CUSTOMERS_API,
    GET_ALL_VENDORS_API,
    GET_ALL_PROJECT_CATEGORIES_API,
    GET_ALL_PROJECT_SUB_CATEGORIES_API,
    GET_ALL_PROJECT_SUB_CATEGORY_DETAILS_API,
    CREATE_CUSTOMER_API,
    CREATE_VENDOR_API,
    CREATE_PROJECT_CATEGORY_API,
    CREATE_PROJECT_SUB_CATEGORY_API,
    CREATE_PROJECT_SUB_CATEGORY_DETAIL_API
} from '../config/API';
import Loader from '../components/common/Loader';
import { showErrorToast } from '../utils/toastUtils';
import Modal from '../components/common/Modal';
import PaginatedSelect from '../components/common/PaginatedSelect';


const AddProject = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedVendors, setSelectedVendors] = useState([]);

    // New dropdown states
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubCategory, setSelectedSubCategory] = useState(null);
    const [selectedDetail, setSelectedDetail] = useState(null);

    // Cache uniqueness to refresh dropdowns after quick add
    const [dropdownCacheUniq, setDropdownCacheUniq] = useState(Date.now());

    // Quick create states
    const [activeModal, setActiveModal] = useState(null);
    const [quickAddFormData, setQuickAddFormData] = useState({});
    const [modalLoading, setModalLoading] = useState(false);

    const [formData, setFormData] = useState({
        projectName: '',
        projectAdd: '',
        googleMapLink: '',
        startDate: new Date(),
        endDate: null,
        description: '',
        status: 'Active'
    });

    // Initial data is handled by PaginatedSelect
    const refreshDropdowns = useCallback(() => {
        setDropdownCacheUniq(Date.now());
    }, []);

    const handleQuickAddChange = (e) => {
        const { name, value } = e.target;
        setQuickAddFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleQuickAddSelectChange = (selectedOption, field) => {
        setQuickAddFormData(prev => ({ ...prev, [field]: selectedOption }));
    };

    const handleQuickAddSubmit = async () => {
        const token = localStorage.getItem('accessToken');
        let apiEndpoint = '';
        let payload = {};
        let successMsg = '';

        if (activeModal === 'customer') {
            if (!quickAddFormData.customerName) return showErrorToast('Customer name is required');
            if (!quickAddFormData.customerPhone) return showErrorToast('Customer phone is required');
            apiEndpoint = CREATE_CUSTOMER_API;
            payload = {
                customerName: quickAddFormData.customerName,
                customerPhone: quickAddFormData.customerPhone,
                customerEmail: quickAddFormData.customerEmail || '',
                customerAdd: quickAddFormData.customerAdd || '',
                status: 'active'
            };
            successMsg = 'Customer created successfully';
        } else if (activeModal === 'vendor') {
            if (!quickAddFormData.vendorName) return showErrorToast('Vendor name is required');
            if (!quickAddFormData.whatsappNumber) return showErrorToast('WhatsApp number is required');
            apiEndpoint = CREATE_VENDOR_API;
            payload = {
                companyName: quickAddFormData.companyName || '',
                name: quickAddFormData.vendorName,
                contactPerson: quickAddFormData.contactPerson || '',
                whatsappNumber: quickAddFormData.whatsappNumber,
                address: quickAddFormData.address || '',
                category: quickAddFormData.category || '',
                gst: quickAddFormData.gst || '',
                pan: quickAddFormData.pan || '',
                status: 'Active'
            };
            successMsg = 'Vendor created successfully';
        } else if (activeModal === 'category') {
            if (!quickAddFormData.categoryName) return showErrorToast('Category name is required');
            apiEndpoint = CREATE_PROJECT_CATEGORY_API;
            payload = { categoryName: quickAddFormData.categoryName, status: 'active' };
            successMsg = 'Category created successfully';
        } else if (activeModal === 'subCategory') {
            if (!quickAddFormData.categoryName) return showErrorToast('Sub-category name is required');
            apiEndpoint = CREATE_PROJECT_SUB_CATEGORY_API;
            payload = { categoryName: quickAddFormData.categoryName, status: 'active' };
            successMsg = 'Sub-category created successfully';
        } else if (activeModal === 'detail') {
            if (!quickAddFormData.detailName) return showErrorToast('BHK/Detail name is required');
            apiEndpoint = CREATE_PROJECT_SUB_CATEGORY_DETAIL_API;
            payload = { detailName: quickAddFormData.detailName, status: 'active' };
            successMsg = 'BHK/Detail created successfully';
        }

        try {
            setModalLoading(true);
            const res = await axios.post(apiEndpoint, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data.status) {
                const newData = res.data.data;
                const modalType = activeModal;
                const id = newData?.id || newData?._id || (typeof newData === 'string' || typeof newData === 'number' ? newData : null);

                // Use input data for label to ensure immediate match with user entry
                let label = '';
                if (modalType === 'customer') label = quickAddFormData.customerName;
                else if (modalType === 'vendor') label = quickAddFormData.vendorName;
                else if (modalType === 'category' || modalType === 'subCategory') label = quickAddFormData.categoryName;
                else if (modalType === 'detail') label = quickAddFormData.detailName;

                // Fallback to newData if label is missing from form
                if (!label && newData && typeof newData === 'object') {
                    label = newData.customerName || newData.vendorName || newData.categoryName || newData.subCategoryName || newData.detailName || newData.name;
                }

                if (id && label) {
                    const newOption = { value: id, label: label };
                    if (modalType === 'customer') setSelectedCustomer(newOption);
                    else if (modalType === 'vendor') setSelectedVendors(prev => [...prev, newOption]);
                    else if (modalType === 'category') setSelectedCategory(newOption);
                    else if (modalType === 'subCategory') setSelectedSubCategory(newOption);
                    else if (modalType === 'detail') setSelectedDetail(newOption);
                }

                // Refresh dropdown data in the background
                refreshDropdowns();

                Swal.fire('Success', successMsg, 'success', {
                    timer: 1500,
                    showConfirmButton: false,
                });

                setActiveModal(null);
                setQuickAddFormData({});
            } else {
                showErrorToast(res.data.message || 'Failed to create');
            }
        } catch (error) {
            console.error('Quick create error:', error);
            showErrorToast(error.response?.data?.message || 'Error creating new entry');
        } finally {
            setModalLoading(false);
        }
    };

    const handleCustomerChange = (selectedOption) => {
        setSelectedCustomer(selectedOption);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDateChange = (date, field) => {
        setFormData(prev => ({
            ...prev,
            [field]: date
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();


        const result = await Swal.fire({
            title: 'Save Project?',
            text: 'Do you want to save this project?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Save',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#4f46e5',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-confirm-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const token = localStorage.getItem('accessToken');

                const payload = {
                    ...formData,
                    customerId: selectedCustomer?.value,
                    vendorIds: selectedVendors.map(v => v.value),
                    projectCatId: selectedCategory?.value,
                    projectSubCatId: selectedSubCategory?.value,
                    projectSubCatDetailId: selectedDetail?.value,
                    startDate: formData.startDate ? formData.startDate.toISOString().split('T')[0] : null,
                    endDate: formData.endDate ? formData.endDate.toISOString().split('T')[0] : null
                };

                const response = await axios.post(CREATE_PROJECT_API, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Saved!',
                        text: 'Project saved successfully',
                        timer: 2000,
                        showConfirmButton: false,
                    });
                    navigate('/projects');
                } else {
                    showErrorToast(response.data.error || response.data.message || 'Failed to save project');
                }
            } catch (error) {
                console.error('Error saving project:', error);
                const errorData = error.response?.data?.errors || error.response?.data?.error || error.response?.data?.message || 'An error occurred';
                showErrorToast(errorData);
            } finally {
                setLoading(false);
            }
        }
    };

    if (loading) {
        return <Loader fullPage text="Saving project..." />;
    }

    return (
        <div className="add-project-page">
            <div className="page-header">
                <button onClick={() => navigate('/projects')} className="back-btn">
                    <MdArrowBack />
                </button>
                <h2>Add New Project</h2>
            </div>

            <form onSubmit={handleSubmit} className="project-form">
                <div className="form-section">
                    <h3>Customer Details</h3>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Select Customer <span className="required-star">*</span></label>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <PaginatedSelect
                                    apiUrl={GET_ALL_CUSTOMERS_API}
                                    mapResponse={(cust) => ({ value: cust.id || cust._id, label: cust.customerName })}
                                    onChange={handleCustomerChange}
                                    value={selectedCustomer}
                                    placeholder="-- Select a Customer --"
                                    isClearable
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    cacheUniq={dropdownCacheUniq}
                                />
                                <button type="button" onClick={() => setActiveModal('customer')} className="quick-add-btn" title="Add Customer">
                                    <MdAdd size={20} />
                                </button>
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Select Vendor(s)</label>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <PaginatedSelect
                                    apiUrl={GET_ALL_VENDORS_API}
                                    mapResponse={(v) => ({
                                        value: v.id || v._id,
                                        label: v.category
                                            ? `${v.companyName} (${v.category})`
                                            : v.companyName
                                    })}
                                    onChange={(selected) => setSelectedVendors(selected || [])}
                                    value={selectedVendors}
                                    placeholder="-- Select Vendor(s) --"
                                    isMulti
                                    isClearable
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    cacheUniq={dropdownCacheUniq}
                                />
                                <button type="button" onClick={() => setActiveModal('vendor')} className="quick-add-btn" title="Add Vendor">
                                    <MdAdd size={20} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h3>Project Details</h3>
                    <div className="form-grid">
                        <div className="form-group full-width">
                            <label>Project Name <span className="required-star">*</span></label>
                            <input
                                type="text"
                                name="projectName"
                                value={formData.projectName}
                                onChange={handleChange}
                                placeholder="Enter project name"
                            />
                        </div>
                        <div className="form-group">
                            <label>Project Type Category</label>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <PaginatedSelect
                                    apiUrl={GET_ALL_PROJECT_CATEGORIES_API}
                                    mapResponse={(item) => ({ value: item.id || item._id, label: item.categoryName })}
                                    onChange={setSelectedCategory}
                                    value={selectedCategory}
                                    placeholder="Select Category"
                                    isClearable
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    cacheUniq={dropdownCacheUniq}
                                />
                                <button type="button" onClick={() => setActiveModal('category')} className="quick-add-btn" title="Add Category">
                                    <MdAdd size={20} />
                                </button>
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Project Type Sub-Category</label>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <PaginatedSelect
                                    apiUrl={GET_ALL_PROJECT_SUB_CATEGORIES_API}
                                    mapResponse={(item) => ({ value: item.id || item._id, label: item.categoryName || item.subCategoryName })}
                                    onChange={(selected) => {
                                        setSelectedSubCategory(selected);
                                        if (selected?.label?.toLowerCase() !== 'apartment') {
                                            setSelectedDetail(null);
                                        }
                                    }}
                                    value={selectedSubCategory}
                                    placeholder="Select Sub-Category"
                                    isClearable
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    cacheUniq={dropdownCacheUniq}
                                />
                                <button type="button" onClick={() => setActiveModal('subCategory')} className="quick-add-btn" title="Add Sub-Category">
                                    <MdAdd size={20} />
                                </button>
                            </div>
                        </div>

                        {selectedSubCategory?.label?.toLowerCase() === 'apartment' && (
                            <div className="form-group">
                                <label>BHK (Project Type Sub-Category Detail)</label>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <PaginatedSelect
                                        apiUrl={GET_ALL_PROJECT_SUB_CATEGORY_DETAILS_API}
                                        mapResponse={(item) => ({ value: item.id || item._id, label: item.detailName || item.detail_name || item.name })}
                                        onChange={setSelectedDetail}
                                        value={selectedDetail}
                                        placeholder="Select BHK / Detail"
                                        isClearable
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        cacheUniq={dropdownCacheUniq}
                                    />
                                    <button type="button" onClick={() => setActiveModal('detail')} className="quick-add-btn" title="Add Detail">
                                        <MdAdd size={20} />
                                    </button>
                                </div>
                            </div>
                        )}
                        <div className="form-group full-width">
                            <label>Project Address / Location <span className="required-star">*</span></label>
                            <input
                                type="text"
                                name="projectAdd"
                                value={formData.projectAdd}
                                onChange={handleChange}
                                placeholder="Enter project location"
                            />
                        </div>
                        <div className="form-group full-width">
                            <label>Google Map Link</label>
                            <input
                                type="text"
                                name="googleMapLink"
                                value={formData.googleMapLink}
                                onChange={handleChange}
                                placeholder="Enter Google Map Link"
                            />
                        </div>
                        <div className="form-group full-width">
                            <label>Description</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows="4"
                                placeholder="Enter project description"
                            ></textarea>
                        </div>
                        <div className="form-group">
                            <label>Start Date (Optional)</label>
                            <DatePicker
                                selected={formData.startDate}
                                onChange={(date) => handleDateChange(date, 'startDate')}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                placeholderText="Select start date"
                                isClearable
                            />
                        </div>
                        <div className="form-group">
                            <label>End Date (Optional)</label>
                            <DatePicker
                                selected={formData.endDate}
                                onChange={(date) => handleDateChange(date, 'endDate')}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                placeholderText="Select end date"
                                isClearable
                            />
                        </div>
                        <div className="form-group">
                            <label>Status <span className="required-star">*</span></label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                            >
                                <option value="Active">Active</option>
                                <option value="Completed">Completed</option>
                            </select>
                        </div>
                    </div>
                </div>
                <div className="form-actions">
                    <button type="button" onClick={() => navigate('/projects')} className="cancel-btn">
                        Cancel
                    </button>
                    <button type="submit" className="save-btn">
                        <MdSave /> Save Project
                    </button>
                </div>
            </form >

            {/* Quick Create Modals */}
            <Modal
                isOpen={!!activeModal}
                onClose={() => { setActiveModal(null); setQuickAddFormData({}); }}
                title={`Quick Create ${activeModal === 'customer' ? 'Customer' : activeModal === 'vendor' ? 'Vendor' : activeModal === 'category' ? 'Category' : activeModal === 'subCategory' ? 'Sub-Category' : 'BHK/Detail'}`}
                footer={(
                    <div className="modal-footer">
                        <button className="swal-cancel-btn" onClick={() => { setActiveModal(null); setQuickAddFormData({}); }}>Cancel</button>
                        <button className="swal-confirm-btn" onClick={handleQuickAddSubmit} disabled={modalLoading}>
                            {modalLoading ? 'Creating...' : 'Create Item'}
                        </button>
                    </div>
                )}
            >
                <div className="form-grid" style={{ gridTemplateColumns: activeModal === 'vendor' || activeModal === 'customer' ? '1fr 1fr' : '1fr' }}>
                    {activeModal === 'customer' && (
                        <>
                            <div className="form-group full-width">
                                <label>Customer Name <span className="required-star">*</span></label>
                                <input
                                    type="text"
                                    name="customerName"
                                    className="form-control"
                                    value={quickAddFormData.customerName || ''}
                                    onChange={handleQuickAddChange}
                                    placeholder="Enter customer name"
                                />
                            </div>
                            <div className="form-group">
                                <label>Phone No <span className="required-star">*</span></label>
                                <input
                                    type="tel"
                                    name="customerPhone"
                                    className="form-control"
                                    value={quickAddFormData.customerPhone || ''}
                                    onChange={handleQuickAddChange}
                                    placeholder="Enter phone number"
                                />
                            </div>
                            <div className="form-group">
                                <label>Email</label>
                                <input
                                    type="email"
                                    name="customerEmail"
                                    className="form-control"
                                    value={quickAddFormData.customerEmail || ''}
                                    onChange={handleQuickAddChange}
                                    placeholder="Enter email address"
                                />
                            </div>
                            <div className="form-group full-width">
                                <label>Customer Address</label>
                                <textarea
                                    name="customerAdd"
                                    className="form-control"
                                    value={quickAddFormData.customerAdd || ''}
                                    onChange={handleQuickAddChange}
                                    placeholder="Enter full address"
                                    rows="2"
                                />
                            </div>
                        </>
                    )}

                    {activeModal === 'vendor' && (
                        <>
                            <div className="form-group full-width">
                                <label>Company Name <span className="required-star">*</span></label>
                                <input
                                    type="text"
                                    name="companyName"
                                    className="form-control"
                                    value={quickAddFormData.companyName || ''}
                                    onChange={handleQuickAddChange}
                                    placeholder="Enter company name"
                                />
                            </div>
                            <div className="form-group full-width">
                                <label>Vendor Name <span className="required-star">*</span></label>
                                <input
                                    type="text"
                                    name="vendorName"
                                    className="form-control"
                                    value={quickAddFormData.vendorName || ''}
                                    onChange={handleQuickAddChange}
                                    placeholder="Enter vendor name"
                                />
                            </div>
                            <div className="form-group">
                                <label>Contact Person</label>
                                <input
                                    type="text"
                                    name="contactPerson"
                                    className="form-control"
                                    value={quickAddFormData.contactPerson || ''}
                                    onChange={handleQuickAddChange}
                                    placeholder="Enter contact person"
                                />
                            </div>
                            <div className="form-group">
                                <label>WhatsApp Number <span className="required-star">*</span></label>
                                <input
                                    type="tel"
                                    name="whatsappNumber"
                                    className="form-control"
                                    value={quickAddFormData.whatsappNumber || ''}
                                    onChange={handleQuickAddChange}
                                    placeholder="Enter WhatsApp number"
                                />
                            </div>
                            <div className="form-group full-width">
                                <label>Address</label>
                                <input
                                    type="text"
                                    name="address"
                                    className="form-control"
                                    value={quickAddFormData.address || ''}
                                    onChange={handleQuickAddChange}
                                    placeholder="Enter full address"
                                />
                            </div>
                            <div className="form-group">
                                <label>Category</label>
                                <input
                                    type="text"
                                    name="category"
                                    className="form-control"
                                    value={quickAddFormData.category || ''}
                                    onChange={handleQuickAddChange}
                                    placeholder="e.g. Electronics"
                                />
                            </div>
                            <div className="form-group">
                                <label>GST Number</label>
                                <input
                                    type="text"
                                    name="gst"
                                    className="form-control"
                                    value={quickAddFormData.gst || ''}
                                    onChange={handleQuickAddChange}
                                    placeholder="Enter GST number"
                                />
                            </div>
                            <div className="form-group">
                                <label>PAN Number</label>
                                <input
                                    type="text"
                                    name="pan"
                                    className="form-control"
                                    value={quickAddFormData.pan || ''}
                                    onChange={handleQuickAddChange}
                                    placeholder="Enter PAN number"
                                />
                            </div>
                        </>
                    )}

                    {(activeModal === 'category' || activeModal === 'subCategory' || activeModal === 'detail') && (
                        <div className="form-group">
                            <label>
                                {activeModal === 'category' ? 'Category' : activeModal === 'subCategory' ? 'Sub-Category' : 'BHK/Detail'} Name <span className="required-star">*</span>
                            </label>
                            <input
                                type="text"
                                name={activeModal === 'category' || activeModal === 'subCategory' ? 'categoryName' : 'detailName'}
                                className="form-control"
                                value={quickAddFormData[activeModal === 'category' || activeModal === 'subCategory' ? 'categoryName' : 'detailName'] || ''}
                                onChange={handleQuickAddChange}
                                placeholder={`Enter ${activeModal} name`}
                            />
                        </div>
                    )}
                </div>
            </Modal>
        </div >
    );
};

export default AddProject;
